import { useLayoutEffect, useRef, useState } from 'react';

/**
 * The Creatomate preview iframe rasterizes its canvas at the iframe's on-screen
 * CSS-pixel box. In a small box (~200-500px) that produces a low-resolution,
 * blurry raster even though the composition is authored at up to 1080px — the
 * exported file renders server-side at full resolution and is sharp, so the
 * blur is preview-only.
 *
 * To fix it we supersample: measure the footprint, size the iframe host to a
 * higher pixel box (footprint * supersample factor), and CSS-scale it back down
 * so the browser downsamples a crisp raster. The supersample factor is the
 * device pixel ratio, capped so the larger backing dimension never exceeds
 * `nativePx` (beyond the composition's native resolution there's nothing sharper
 * to gain) and never below 1 (never render below the display size).
 *
 * Attach `footprintRef` to the footprint element and apply `width/height` +
 * `transform: scale(scale)` (origin top-left) to the absolutely-positioned
 * iframe host.
 */
export function usePreviewSupersample(nativePx = 1080) {
  const footprintRef = useRef<HTMLDivElement>(null);
  const [{ width, height, scale }, setBox] = useState({
    width: 0,
    height: 0,
    scale: 0,
  });

  useLayoutEffect(() => {
    const el = footprintRef.current;
    if (!el) return;

    const measure = () => {
      const w = el.clientWidth;
      const h = el.clientHeight;
      if (!w || !h) return;
      const dpr = window.devicePixelRatio || 1;
      const factor = Math.max(1, Math.min(dpr, nativePx / Math.max(w, h)));
      setBox({
        width: Math.round(w * factor),
        height: Math.round(h * factor),
        scale: 1 / factor,
      });
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(el);
    return () => observer.disconnect();
  }, [nativePx]);

  return { footprintRef, width, height, scale };
}
